import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { http } from '@/lib/api';
import { ArrowLeft, ArrowRight, Heart, CheckCircle2 } from 'lucide-react';

const SCALE = [
  { value: 1, label: 'Strongly Disagree' },
  { value: 2, label: 'Disagree' },
  { value: 3, label: 'Neutral' },
  { value: 4, label: 'Agree' },
  { value: 5, label: 'Strongly Agree' },
];

export default function AttachmentAssessment() {
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    http.get('/attachment/questions').then(r => {
      setItems(r.data.items);
      setMeta(r.data.meta);
    });
  }, []);

  const q = items[i];
  const progress = items.length ? ((i + 1) / items.length) * 100 : 0;

  const answer = (val) => {
    setAnswers(a => ({ ...a, [q.id]: val }));
    // Auto-advance
    setTimeout(() => {
      if (i < items.length - 1) setI(i + 1);
    }, 180);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await http.post('/attachment/submit', { answers });
      // Redirect to the hub — it shows result + AI advice + recommendations in one place.
      nav('/app/attachment');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit');
      setSubmitting(false);
    }
  };

  const answeredCount = Object.keys(answers).length;

  if (!q) return <div className="p-10 text-navy/60">Loading…</div>;

  const isLast = i === items.length - 1;
  const canSubmit = answeredCount >= Math.min(items.length, 12);

  return (
    <div className="min-h-screen bg-[#F9F8F6] p-6 lg:p-10 flex flex-col" data-testid="attachment-assessment">
      <div className="max-w-2xl lg:max-w-3xl mx-auto w-full flex-1 flex flex-col">
        <button onClick={()=>nav('/app/attachment')} data-testid="attach-back" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy mb-4 self-start">
          <ArrowLeft size={14}/> Back
        </button>

        {/* Header + progress */}
        <div className="mb-4 lg:mb-6 flex items-center justify-between text-xs lg:text-sm text-navy/60">
          <span className="inline-flex items-center gap-2"><Heart size={14} className="text-gold"/> Attachment Assessment</span>
          <span data-testid="attach-step-indicator">Question {i + 1} of {items.length}</span>
        </div>
        <div className="h-1 bg-navy/10 rounded-full overflow-hidden mb-8 lg:mb-10">
          <div className="h-full bg-gold transition-all" style={{ width: `${progress}%` }} data-testid="attach-progress-bar"/>
        </div>

        {/* Question card */}
        <div className="bg-white rounded-3xl p-8 lg:p-12 card-soft" data-testid={`attach-q-${q.id}`}>
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-4">In relationships</p>
          <h2 className="font-heading text-2xl lg:text-3xl leading-snug mb-8 lg:mb-10">&quot;{q.text}&quot;</h2>
          <div className="space-y-2 lg:space-y-3">
            {SCALE.map(s => (
              <button
                key={s.value}
                onClick={()=>answer(s.value)}
                data-testid={`attach-answer-${q.id}-${s.value}`}
                className={`w-full text-left p-4 lg:p-5 rounded-2xl border transition-smooth text-sm lg:text-base ${
                  answers[q.id]===s.value ? 'bg-navy text-white border-navy' : 'bg-beige border-transparent hover:bg-[#EAE5D9]'
                }`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Prev / Next / Submit */}
        <div className="flex items-center justify-between mt-6 lg:mt-8 gap-3">
          <button
            onClick={()=>setI(Math.max(0, i-1))}
            disabled={i === 0}
            data-testid="attach-prev"
            className="inline-flex items-center gap-2 text-sm text-navy/60 disabled:opacity-30 hover:text-navy">
            <ArrowLeft size={14}/> Previous
          </button>
          <p className="text-xs lg:text-sm text-navy/50">{answeredCount} of {items.length} answered</p>
          {isLast ? (
            <button
              onClick={submit}
              disabled={!canSubmit || submitting}
              data-testid="attach-submit"
              className="inline-flex items-center gap-2 bg-navy text-white rounded-full px-6 py-3 hover:bg-navy-soft disabled:opacity-40">
              {submitting ? 'Scoring…' : 'See my results'} <CheckCircle2 size={14}/>
            </button>
          ) : (
            <button
              onClick={()=>setI(Math.min(items.length-1, i+1))}
              disabled={!answers[q.id]}
              data-testid="attach-next"
              className="inline-flex items-center gap-2 bg-navy text-white rounded-full px-6 py-3 hover:bg-navy-soft disabled:opacity-40">
              Next <ArrowRight size={14}/>
            </button>
          )}
        </div>

        <p className="text-xs text-navy/50 mt-8 text-center">
          Educational, not diagnostic. This is a supportive reflection tool — not a clinical test.
          Adapted from Experiences in Close Relationships—Revised (ECR-R) research, framed with cultural context.
        </p>
      </div>
    </div>
  );
}
